// Shared conversation renderer for both the user and admin support surfaces.
// Staff messages sit on the left with a "DAGsight Support" badge; the
// counterpart's messages sit on the right. `viewer` flips which side the
// reader's own messages land on:
//   - viewer='customer' → non-staff messages are "mine" (right)
//   - viewer='staff'     → staff messages are "mine" (right)

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { TicketMessage } from '@/lib/support';

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

interface TicketThreadProps {
  messages: TicketMessage[];
  viewer: 'customer' | 'staff';
}

export function TicketThread({ messages, viewer }: TicketThreadProps) {
  if (messages.length === 0) {
    return (
      <div className="rounded-md border border-dashed py-10 text-center text-sm text-muted-foreground">
        No messages yet.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {messages.map((m) => {
        const mine = viewer === 'staff' ? m.is_staff : !m.is_staff;
        return (
          <div key={m.id} className={cn('flex', mine ? 'justify-end' : 'justify-start')}>
            <div className={cn('max-w-[80%] space-y-1', mine ? 'items-end text-right' : '')}>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                {m.is_staff ? (
                  <Badge variant="secondary" className="font-normal">
                    DAGsight Support
                  </Badge>
                ) : (
                  <span className="font-medium">{m.author_email}</span>
                )}
                <span>{fmtDate(m.created_at)}</span>
              </div>
              <div
                className={cn(
                  'inline-block whitespace-pre-wrap rounded-lg px-3 py-2 text-left text-sm',
                  mine
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-foreground',
                )}
              >
                {m.body}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
